import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Attachment } from '../../stores/inbox-store';

/**
 * Bijlagen van één bericht. Binaries komen via één providerbewuste API en
 * worden met een Authorization-header opgehaald; de sessietoken staat nooit in
 * een img-src, browserhistorie of foutmelding.
 */

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function isImageMimeType(mimeType: string): boolean {
  return mimeType.toLowerCase().startsWith('image/');
}

export function inboxAttachmentRequest(attachmentId: string, token: string): {
  url: string;
  init: RequestInit;
} {
  return {
    url: `/api/inbox/attachment?id=${encodeURIComponent(attachmentId)}`,
    init: {
      credentials: 'same-origin',
      headers: { Authorization: `Bearer ${token}` },
    },
  };
}

export type AttachmentMediaState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'verifying'; url: string }
  | { status: 'loaded'; url: string }
  | { status: 'error'; message: string };

export type AttachmentMediaAction =
  | { type: 'load' }
  | { type: 'fetched'; url: string }
  | { type: 'verified'; url: string; naturalWidth: number }
  | { type: 'failed'; message?: string };

export function attachmentMediaReducer(
  state: AttachmentMediaState,
  action: AttachmentMediaAction,
): AttachmentMediaState {
  switch (action.type) {
    case 'load':
      return { status: 'loading' };
    case 'fetched':
      return { status: 'verifying', url: action.url };
    case 'verified':
      return action.naturalWidth > 0
        ? { status: 'loaded', url: action.url }
        : { status: 'error', message: 'Afbeelding kon niet worden geladen' };
    case 'failed':
      return { status: 'error', message: action.message || 'Afbeelding kon niet worden geladen' };
    default:
      return state;
  }
}

export function kanAfbeeldingOpenen(state: AttachmentMediaState | undefined): state is { status: 'loaded'; url: string } {
  return state?.status === 'loaded' && Boolean(state.url);
}

export function useAuthToken(): string | null | undefined {
  const [token, setToken] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    let actief = true;
    import('../../lib/supabase')
      .then(({ supabase }) => supabase.auth.getSession())
      .then(({ data }) => {
        if (actief) setToken(data.session?.access_token ?? null);
      })
      .catch(() => {
        if (actief) setToken(null);
      });
    return () => { actief = false; };
  }, []);
  return token;
}

async function foutUitResponse(response: Response): Promise<string> {
  try {
    const body = await response.json() as { error?: unknown };
    if (typeof body.error === 'string' && body.error.trim()) return body.error;
  } catch {
    // Een binaire proxy kan bij een infrastructuurfout ook een lege body geven.
  }
  return 'Bijlage kon niet worden opgehaald.';
}

async function haalBijlageBlobOp(att: Attachment, token: string): Promise<Blob> {
  const request = inboxAttachmentRequest(att.id, token);
  const response = await fetch(request.url, request.init);
  if (!response.ok) throw new Error(await foutUitResponse(response));
  const blob = await response.blob();
  if (blob.size === 0) throw new Error('De bijlage is leeg.');
  if (isImageMimeType(att.mime_type) && blob.type && !blob.type.toLowerCase().startsWith('image/')) {
    throw new Error('De bijlage is geen geldige afbeelding.');
  }
  return blob;
}

function activeerDownload(url: string, filename: string): void {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

interface AttachmentImageTileProps {
  attachment: Attachment;
  state: AttachmentMediaState;
  groot: boolean;
  downloadBusy: boolean;
  downloadError?: string;
  onOpen: () => void;
  onRetry: () => void;
  onDownload: () => void;
  onVerified: (url: string, naturalWidth: number) => void;
  onFailed: () => void;
}

/** Pure presentatielaag, zodat fout/retry/download zonder browsernetwerk testbaar is. */
export function AttachmentImageTile({
  attachment: att,
  state,
  groot,
  downloadBusy,
  downloadError,
  onOpen,
  onRetry,
  onDownload,
  onVerified,
  onFailed,
}: AttachmentImageTileProps) {
  const thumb = groot ? 'w-48 h-48' : 'w-32 h-32';

  if (state.status === 'error') {
    return (
      <div className={`${thumb} flex flex-col justify-center rounded-lg border border-red-200 bg-red-50 p-3`} role="alert">
        <p className="text-xs font-medium text-red-800">Afbeelding kon niet worden geladen</p>
        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
          <button type="button" onClick={onRetry} className="text-xs font-medium text-red-700 underline">
            Opnieuw proberen
          </button>
          <button type="button" disabled={downloadBusy} onClick={onDownload} className="text-xs text-slate-700 underline disabled:opacity-50">
            {downloadBusy ? 'Downloaden…' : 'Downloaden'}
          </button>
        </div>
        {downloadError && <p className="mt-1 text-[11px] text-red-700">{downloadError}</p>}
      </div>
    );
  }

  const url = state.status === 'verifying' || state.status === 'loaded' ? state.url : null;
  const bewezen = kanAfbeeldingOpenen(state);
  return (
    <button
      type="button"
      disabled={!bewezen}
      onClick={onOpen}
      className={`group relative overflow-hidden rounded-lg border border-slate-200 transition-all ${thumb} ${bewezen ? 'hover:border-indigo-400 hover:shadow-md' : 'cursor-wait bg-slate-100'}`}
      title={bewezen ? `${att.filename} (klik om te vergroten)` : `Afbeelding laden: ${att.filename}`}
      aria-label={bewezen ? `${att.filename} vergroten` : `${att.filename} laden`}
      aria-busy={!bewezen}
    >
      {url ? (
        <img
          src={url}
          alt={att.filename}
          className="h-full w-full object-cover"
          onLoad={(event) => onVerified(url, event.currentTarget.naturalWidth)}
          onError={onFailed}
        />
      ) : (
        <span className="text-xs text-slate-500">Afbeelding laden…</span>
      )}
      {bewezen && (
        <span className="absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/60 to-transparent px-2 py-1 text-left text-[11px] text-white opacity-0 transition-opacity group-hover:opacity-100">
          {att.filename}
        </span>
      )}
    </button>
  );
}

export function AttachmentList({ attachments, groot = false }: { attachments: Attachment[]; groot?: boolean }) {
  const [openId, setOpenId] = useState<string | null>(null);
  const [media, setMedia] = useState<Record<string, AttachmentMediaState>>({});
  const [downloadBusy, setDownloadBusy] = useState<string | null>(null);
  const [downloadErrors, setDownloadErrors] = useState<Record<string, string>>({});
  const token = useAuthToken();
  const mediaRef = useRef<Record<string, AttachmentMediaState>>({});
  const requestVersies = useRef(new Map<string, number>());
  const actief = useRef(true);

  const images = useMemo(() => attachments.filter((a) => isImageMimeType(a.mime_type)), [attachments]);
  const files = useMemo(() => attachments.filter((a) => !isImageMimeType(a.mime_type)), [attachments]);

  const zetMedia = useCallback((id: string, state: AttachmentMediaState) => {
    mediaRef.current = { ...mediaRef.current, [id]: state };
    setMedia(mediaRef.current);
  }, []);

  const trekUrlIn = useCallback((id: string) => {
    const oud = mediaRef.current[id];
    if (oud?.status === 'verifying' || oud?.status === 'loaded') URL.revokeObjectURL(oud.url);
  }, []);

  const laadAfbeelding = useCallback(async (att: Attachment, force = false) => {
    if (!token) return;
    const huidig = mediaRef.current[att.id];
    if (!force && huidig && huidig.status !== 'idle' && huidig.status !== 'error') return;

    trekUrlIn(att.id);
    const versie = (requestVersies.current.get(att.id) ?? 0) + 1;
    requestVersies.current.set(att.id, versie);
    zetMedia(att.id, attachmentMediaReducer(huidig ?? { status: 'idle' }, { type: 'load' }));
    try {
      const blob = await haalBijlageBlobOp(att, token);
      const url = URL.createObjectURL(blob);
      if (!actief.current || requestVersies.current.get(att.id) !== versie) {
        URL.revokeObjectURL(url);
        return;
      }
      zetMedia(att.id, attachmentMediaReducer({ status: 'loading' }, { type: 'fetched', url }));
    } catch {
      if (actief.current && requestVersies.current.get(att.id) === versie) {
        zetMedia(att.id, attachmentMediaReducer({ status: 'loading' }, { type: 'failed' }));
      }
    }
  }, [token, trekUrlIn, zetMedia]);

  useEffect(() => {
    if (!token) return;
    for (const att of images) void laadAfbeelding(att);
  }, [images, laadAfbeelding, token]);

  useEffect(() => {
    actief.current = true;
    return () => {
      actief.current = false;
      for (const id of Object.keys(mediaRef.current)) {
        requestVersies.current.set(id, (requestVersies.current.get(id) ?? 0) + 1);
        trekUrlIn(id);
      }
      mediaRef.current = {};
    };
  }, [trekUrlIn]);

  const bewijsAfbeelding = useCallback((id: string, url: string, naturalWidth: number) => {
    const huidig = mediaRef.current[id];
    if (huidig?.status !== 'verifying' || huidig.url !== url) return;
    const volgende = attachmentMediaReducer(huidig, { type: 'verified', url, naturalWidth });
    if (volgende.status === 'error') URL.revokeObjectURL(url);
    zetMedia(id, volgende);
  }, [zetMedia]);

  const markeerAfbeeldingFout = useCallback((id: string) => {
    trekUrlIn(id);
    const versie = (requestVersies.current.get(id) ?? 0) + 1;
    requestVersies.current.set(id, versie);
    zetMedia(id, { status: 'error', message: 'Afbeelding kon niet worden geladen' });
    setOpenId((open) => open === id ? null : open);
  }, [trekUrlIn, zetMedia]);

  const download = useCallback(async (att: Attachment) => {
    if (!token || downloadBusy) return;
    setDownloadBusy(att.id);
    setDownloadErrors((errors) => ({ ...errors, [att.id]: '' }));
    let tijdelijkeUrl: string | null = null;
    try {
      const state = mediaRef.current[att.id];
      const url = kanAfbeeldingOpenen(state)
        ? state.url
        : (tijdelijkeUrl = URL.createObjectURL(await haalBijlageBlobOp(att, token)));
      activeerDownload(url, att.filename);
    } catch {
      setDownloadErrors((errors) => ({ ...errors, [att.id]: 'Downloaden is mislukt. Probeer het opnieuw.' }));
    } finally {
      if (tijdelijkeUrl) window.setTimeout(() => URL.revokeObjectURL(tijdelijkeUrl!), 1_000);
      setDownloadBusy(null);
    }
  }, [downloadBusy, token]);

  const geladenImages = images.filter((att) => kanAfbeeldingOpenen(media[att.id]));
  const openIndex = openId ? geladenImages.findIndex((att) => att.id === openId) : -1;
  const huidig = openIndex >= 0 ? geladenImages[openIndex] : null;
  const huidigState = huidig ? media[huidig.id] : undefined;

  const vorige = useCallback(() => {
    if (openIndex < 0 || geladenImages.length === 0) return;
    setOpenId(geladenImages[(openIndex + geladenImages.length - 1) % geladenImages.length]!.id);
  }, [geladenImages, openIndex]);
  const volgende = useCallback(() => {
    if (openIndex < 0 || geladenImages.length === 0) return;
    setOpenId(geladenImages[(openIndex + 1) % geladenImages.length]!.id);
  }, [geladenImages, openIndex]);

  useEffect(() => {
    if (!huidig) return;
    const toets = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.stopPropagation(); setOpenId(null); }
      else if (event.key === 'ArrowLeft') vorige();
      else if (event.key === 'ArrowRight') volgende();
    };
    window.addEventListener('keydown', toets, true);
    return () => window.removeEventListener('keydown', toets, true);
  }, [huidig, vorige, volgende]);

  if (attachments.length === 0) return null;
  if (token === undefined) return <p className="mt-3 text-xs text-slate-500">Bijlagen laden…</p>;
  if (token === null) {
    return <p role="alert" className="mt-3 text-sm text-red-700">Bijlagen zijn niet beschikbaar. Log opnieuw in.</p>;
  }

  return (
    <>
      <div className="mt-3 space-y-2">
        {images.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {images.map((att) => (
              <AttachmentImageTile
                key={att.id}
                attachment={att}
                state={media[att.id] ?? { status: 'loading' }}
                groot={groot}
                downloadBusy={downloadBusy === att.id}
                downloadError={downloadErrors[att.id]}
                onOpen={() => {
                  if (kanAfbeeldingOpenen(mediaRef.current[att.id])) setOpenId(att.id);
                }}
                onRetry={() => void laadAfbeelding(att, true)}
                onDownload={() => void download(att)}
                onVerified={(url, naturalWidth) => bewijsAfbeelding(att.id, url, naturalWidth)}
                onFailed={() => markeerAfbeeldingFout(att.id)}
              />
            ))}
          </div>
        )}

        {files.map((att) => (
          <div key={att.id} className="max-w-md">
            <button
              type="button"
              disabled={downloadBusy === att.id}
              onClick={() => void download(att)}
              className="group flex w-full items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-left text-sm transition-colors hover:border-slate-300 hover:bg-slate-100 disabled:opacity-60"
            >
              <svg className="h-4 w-4 flex-shrink-0 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13" />
              </svg>
              <span className="truncate text-slate-700">{att.filename}</span>
              <span className="flex-shrink-0 text-xs text-slate-400">{formatFileSize(att.size)}</span>
              <span className="ml-auto flex-shrink-0 text-xs font-medium text-indigo-600">
                {downloadBusy === att.id ? 'Downloaden…' : 'Downloaden'}
              </span>
            </button>
            {downloadErrors[att.id] && <p role="alert" className="mt-1 text-xs text-red-700">{downloadErrors[att.id]}</p>}
          </div>
        ))}
      </div>

      {huidig && kanAfbeeldingOpenen(huidigState) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-6" onClick={() => setOpenId(null)} role="dialog" aria-modal="true" aria-label={`Vergrote afbeelding: ${huidig.filename}`}>
          {geladenImages.length > 1 && (
            <button type="button" onClick={(event) => { event.stopPropagation(); vorige(); }} className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-3 shadow-lg hover:bg-white" title="Vorige (pijl links)">
              <svg className="h-5 w-5 text-slate-700" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            </button>
          )}
          <div className="relative max-h-full max-w-full" onClick={(event) => event.stopPropagation()}>
            <img src={huidigState.url} alt={huidig.filename} className="max-h-[85vh] max-w-full rounded-lg shadow-2xl" />
            <div className="absolute right-3 top-3 flex gap-2">
              <button type="button" disabled={downloadBusy === huidig.id} onClick={() => void download(huidig)} className="rounded-full bg-white/90 p-2 shadow-lg hover:bg-white disabled:opacity-60" title="Downloaden">
                <svg className="h-5 w-5 text-slate-700" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" /></svg>
              </button>
              <button type="button" onClick={() => setOpenId(null)} className="rounded-full bg-white/90 p-2 shadow-lg hover:bg-white" title="Sluiten (Esc)">
                <svg className="h-5 w-5 text-slate-700" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <p className="mt-3 text-center text-sm text-white/80">{huidig.filename}{geladenImages.length > 1 ? ` · ${openIndex + 1} van ${geladenImages.length}` : ''}</p>
          </div>
          {geladenImages.length > 1 && (
            <button type="button" onClick={(event) => { event.stopPropagation(); volgende(); }} className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-3 shadow-lg hover:bg-white" title="Volgende (pijl rechts)">
              <svg className="h-5 w-5 text-slate-700" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </button>
          )}
        </div>
      )}
    </>
  );
}
